import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard,
  FileSearch,
  Target,
  MessagesSquare,
  Trophy,
  Award,
  CreditCard,
  User as UserIcon,
  LogOut,
  Sparkles,
  ShieldCheck,
  TrendingUp,
  Building2,
} from "lucide-react";
import { useAuth } from "../lib/auth";

const links = [
  { to: "/app/dashboard", label: "Dashboard", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/app/resume", label: "Resume Analyzer", icon: FileSearch, testid: "nav-resume" },
  { to: "/app/skillgap", label: "Skill Gap", icon: Target, testid: "nav-skillgap" },
  { to: "/app/interview", label: "Mock Interview", icon: MessagesSquare, testid: "nav-interview" },
  { to: "/app/leaderboard", label: "Leaderboard", icon: Trophy, testid: "nav-leaderboard" },
  { to: "/app/badges", label: "Badges", icon: Award, testid: "nav-badges" },
  { to: "/app/pricing", label: "Pricing", icon: CreditCard, testid: "nav-pricing" },
  { to: "/app/profile", label: "Profile", icon: UserIcon, testid: "nav-profile" },
];

export default function Sidebar() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <aside className="hidden md:flex md:flex-col md:w-64 border-r border-[#E8DDC9] bg-[#EFE7D4] sticky top-0 h-screen">
      <div className="px-6 pt-6 pb-4 flex items-center gap-2">
        <div className="h-8 w-8 bg-[#0A0A0A] text-white rounded-md flex items-center justify-center">
          <Sparkles size={16} />
        </div>
        <div>
          <div className="font-display font-black text-[15px] leading-none">SkillGap Pilot</div>
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500 mt-1">Analyze. Improve.</div>
        </div>
      </div>
      <nav className="px-3 py-2 flex flex-col gap-1">
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            data-testid={l.testid}
            className={({ isActive }) =>
              `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
                isActive
                  ? "bg-[#0A0A0A] text-white"
                  : "text-zinc-700 hover:bg-zinc-200/60"
              }`
            }
          >
            <l.icon size={16} />
            {l.label}
          </NavLink>
        ))}
        {user?.org_role === "admin" && (
          <NavLink
            to="/app/org/dashboard"
            data-testid="nav-org-dashboard"
            className={({ isActive }) =>
              `mt-2 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors border ${
                isActive
                  ? "bg-sky-600 text-white border-sky-600"
                  : "text-sky-700 border-sky-200 bg-sky-50/60 hover:bg-sky-100"
              }`
            }
          >
            <Building2 size={16} />
            {user?.org_account_type === "institution" ? "Institution" : "Organization"}
          </NavLink>
        )}
        {user?.is_admin && (
          <>
            <NavLink
              to="/app/admin"
              data-testid="nav-admin"
              className={({ isActive }) =>
                `mt-2 flex items-center gap-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors border ${
                  isActive
                    ? "bg-indigo-600 text-white border-indigo-600"
                    : "text-indigo-700 border-indigo-200 bg-indigo-50/60 hover:bg-indigo-100"
                }`
              }
            >
              <ShieldCheck size={16} />
              Admin
            </NavLink>
            <NavLink
              to="/app/admin/growth"
              data-testid="nav-growth"
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-semibold transition-colors border ${
                  isActive
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "text-emerald-700 border-emerald-200 bg-emerald-50/60 hover:bg-emerald-100"
                }`
              }
            >
              <TrendingUp size={16} />
              Growth Agents
            </NavLink>
          </>
        )}
      </nav>
      <div className="mt-auto px-3 pb-4">
        <div className="card-flat p-3 mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Signed in</div>
          <div className="font-medium text-sm truncate" data-testid="sidebar-user-name">{user?.name}</div>
          <div className="text-xs text-zinc-500 truncate">{user?.email}</div>
        </div>
        <button
          data-testid="logout-btn"
          onClick={() => { logout(); navigate("/"); }}
          className="w-full flex items-center justify-center gap-2 px-3 py-2 rounded-md text-sm font-medium border border-zinc-200 hover:border-zinc-900 transition"
        >
          <LogOut size={14} /> Logout
        </button>
      </div>
    </aside>
  );
}
